// src/pages/MySignature.jsx
// "İmzam" — every signed-in user (org_user included) sees their own signature
// and, when their admin has allowed more than one, picks the template and
// language. The pick is stored server-side (same rows the Outlook taskpane
// writes), so it applies in Outlook desktop, web AND mobile — Outlook mobile
// has no compose taskpane, so this page is how mobile-only users choose.
import React, { useCallback, useEffect, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Alert, Card, Spinner } from '../components/ui';
import { useT, apiErrorMessage } from '../i18n';
import './MySignature.css';

const messages = {
  loadFailed: { tr: 'İmzanız yüklenemedi.', en: 'Couldn’t load your signature.' },
  saveFailed: { tr: 'Seçiminiz kaydedilemedi.', en: 'Couldn’t save your choice.' },
  docTitle: { tr: 'İmzam - Signature Manager', en: 'My Signature - Signature Manager' },
  title: { tr: 'İmzam', en: 'My Signature' },
  hint: {
    tr: 'Outlook\'ta gönderdiğiniz e-postalara eklenen imzanız. Burada yaptığınız seçim Outlook masaüstü, web ve mobil uygulamada otomatik olarak uygulanır.',
    en: 'The signature added to the emails you send in Outlook. Your choice here applies automatically in Outlook desktop, web and mobile.',
  },
  saved: { tr: 'Seçiminiz kaydedildi — yeni yazdığınız e-postalarda geçerli olacak.', en: 'Your choice is saved — it applies to new emails you write.' },
  preview: { tr: 'Önizleme', en: 'Preview' },
  missing: { tr: 'Henüz size atanmış bir imza yok. Kurum yöneticinizle iletişime geçin.', en: 'No signature has been assigned to you yet. Contact your organization’s administrator.' },
  override: { tr: 'İmzanız kurum yöneticiniz tarafından size özel olarak ayarlanmış.', en: 'Your signature has been set up personally for you by your administrator.' },
  template: { tr: 'Şablon', en: 'Template' },
  templateAria: { tr: 'İmza şablonu', en: 'Signature template' },
  noChoice: { tr: 'Henüz seçim yapmadınız; şu an grubunuza atanmış varsayılan imza kullanılıyor.', en: 'You haven’t made a choice yet; the default signature assigned to your group is being used.' },
  language: { tr: 'Dil', en: 'Language' },
  languageAria: { tr: 'İmza dili', en: 'Signature language' },
  adminDecides: {
    tr: 'İmzanızı kurum yöneticiniz belirliyor. Farklı bir şablon kullanmak isterseniz yöneticinizden size seçenek tanımlamasını isteyebilirsiniz.',
    en: 'Your administrator sets your signature. If you’d like to use a different template, ask your administrator to give you options.',
  },
};

export default function MySignature() {
  const [choices, setChoices] = useState(null);
  const [preview, setPreview] = useState(null); // { html } | { missing: true }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const { t } = useT(messages);

  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const loadPreview = useCallback(async () => {
    try {
      const data = await api.get(endpoints.signatures.mySignaturePreview);
      setPreview({ html: data.template_html || data.html_content || '' });
    } catch (err) {
      if (err.response?.status === 404) setPreview({ missing: true });
      else throw err;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [data] = await Promise.all([api.get(endpoints.signatures.mySignature), loadPreview()]);
        setChoices(data);
      } catch (err) {
        setError(apiErrorMessage(err, messages.loadFailed));
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPreview]);

  const save = async (change) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const data = await api.put(endpoints.signatures.mySignature, change);
      setChoices(data);
      await loadPreview();
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, messages.saveFailed));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-loading"><Spinner /></div>;

  const templates = choices?.templates || [];
  const languages = choices?.languages || [];
  const canPickTemplate = templates.length >= 2;
  const canPickLanguage = languages.length >= 2;

  return (
    <div className="page-stack my-signature">
      <h1>{t('title')}</h1>
      <p className="my-signature-hint">{t('hint')}</p>

      {error && <Alert variant="error">{error}</Alert>}
      {saved && !error && (
        <Alert variant="success">{t('saved')}</Alert>
      )}

      <Card className="my-signature-card">
        <h2 className="my-signature-section-title">{t('preview')}</h2>
        {preview?.missing ? (
          <p className="my-signature-muted">
            {t('missing')}
          </p>
        ) : (
          <div className="my-signature-preview" aria-busy={saving}>
            {/* Same trusted, server-rendered signature HTML the admin panel previews. */}
            <div dangerouslySetInnerHTML={{ __html: preview?.html || '' }} />
          </div>
        )}
      </Card>

      {choices?.is_override && (
        <Alert variant="info">{t('override')}</Alert>
      )}

      {canPickTemplate && (
        <Card className="my-signature-card">
          <h2 className="my-signature-section-title">{t('template')}</h2>
          <div className="my-signature-options" role="radiogroup" aria-label={t('templateAria')}>
            {templates.map(tpl => {
              const selected = choices.current_template_id === tpl.id;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`my-signature-option ${selected ? 'selected' : ''}`.trim()}
                  disabled={saving}
                  onClick={() => !selected && save({ template_id: tpl.id })}
                >
                  {tpl.name}
                </button>
              );
            })}
          </div>
          {!choices.current_template_id && (
            <p className="my-signature-muted">
              {t('noChoice')}
            </p>
          )}
        </Card>
      )}

      {canPickLanguage && (
        <Card className="my-signature-card">
          <h2 className="my-signature-section-title">{t('language')}</h2>
          <div className="my-signature-options" role="radiogroup" aria-label={t('languageAria')}>
            {languages.map(l => {
              const selected = choices.current_language === l.language;
              return (
                <button
                  key={l.language}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`my-signature-option ${selected ? 'selected' : ''}`.trim()}
                  disabled={saving}
                  onClick={() => !selected && save({ language: l.language })}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {!canPickTemplate && !canPickLanguage && !choices?.is_override && !preview?.missing && (
        <p className="my-signature-muted">
          {t('adminDecides')}
        </p>
      )}
    </div>
  );
}
